import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer, Work, BrandMarquee } from "@/components/site/Sections";
import {
  Gamepad2,
  ShoppingBag,
  MapPin,
  Building2,
  Stethoscope,
  Zap,
  Laptop,
  Landmark,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Layers,
  Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/work")({
  head: () => ({
    meta: [
      {
        title: "Industries & Case Work — KMWAdvertising · Strategic Media Systems",
      },
      {
        name: "description",
        content:
          "Explore the vertical-specific media systems and growth frameworks KMWAdvertising builds across Gaming, FMCG, Real Estate, Healthcare, D2C, Hospitality, and Enterprise.",
      },
      {
        property: "og:title",
        content: "Industries & Case Work — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "We build vertical-specific media systems tailored for measurable business growth across traditional and digital channels.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WorkPage,
});

const industryDetails = [
  {
    name: "Gaming & Entertainment",
    tag: "High Engagement · User Acquisition",
    icon: Gamepad2,
    gradient: "linear-gradient(135deg, oklch(0.7 0.22 40), oklch(0.55 0.22 25))",
    shadow: "rgba(249, 115, 22, 0.4)",
    overview:
      "Precision user acquisition, player retention, and hyper-targeted digital and broadcast campaigns for gaming studios, esports leagues, and digital entertainment platforms.",
    channels: ["Programmatic Ads", "OTT & Connected TV", "Performance Social", "Influencer Media"],
    metric: "3.4x Player LTV Growth",
    highlights: [
      "Real-time bid optimization across programmatic gaming exchanges",
      "Dynamic creative localization across Tier-1, Tier-2, and Tier-3 markets",
      "Compliant ad delivery adhering strictly to regional advertising standards",
    ],
  },
  {
    name: "FMCG & Consumer Brands",
    tag: "Mass Market · High Reach",
    icon: ShoppingBag,
    gradient: "linear-gradient(135deg, oklch(0.62 0.22 300), oklch(0.55 0.2 340))",
    shadow: "rgba(168, 85, 247, 0.4)",
    overview:
      "High-impact media buying across national and regional TV, digital display, print networks, and retail environments to drive mass brand recall and offline shelf velocity.",
    channels: ["Prime-Time TV", "Outdoor (OOH)", "Regional Radio", "Digital Display & Video"],
    metric: "Omnichannel Scale",
    highlights: [
      "Prime-time TV spot negotiations with premier broadcast networks",
      "High-frequency transit and billboard placements in key metropolitan hubs",
      "Integrated regional language creative adaptations for maximum penetration",
    ],
  },
  {
    name: "Hospitality & Travel",
    tag: "Direct Bookings · High ROAS",
    icon: MapPin,
    gradient: "linear-gradient(135deg, oklch(0.78 0.14 220), oklch(0.45 0.15 250))",
    shadow: "rgba(6, 182, 212, 0.4)",
    overview:
      "Performance flight campaigns that capture high-intent travelers, driving direct booking revenue and seasonal occupancy increases while reducing OTA commission dependency.",
    channels: ["Search Engine Marketing", "Meta Travel Ads", "Programmatic Display", "Social Media"],
    metric: "High ROAS Flights",
    highlights: [
      "Intent-based search capture targeting seasonal and business travelers",
      "Cross-device retargeting designed to convert abandoned reservation sessions",
      "Geo-fenced mobile campaigns around airports, luxury hubs, and transit centers",
    ],
  },
  {
    name: "Real Estate & Infrastructure",
    tag: "High-Intent Leads · HNI Reach",
    icon: Building2,
    gradient: "linear-gradient(135deg, oklch(0.72 0.19 50), oklch(0.62 0.22 300))",
    shadow: "rgba(234, 179, 8, 0.4)",
    overview:
      "Geo-targeted buyer acquisition, luxury property showcases, and verified investor pipelines designed to generate high-converting site visits and closed bookings.",
    channels: ["Targeted Social Ads", "Print & Lifestyle Media", "Premium OOH", "Landing Page CRO"],
    metric: "Verified Pipeline",
    highlights: [
      "HNI demographic filtering and micro-location radius targeting",
      "High-converting 3D architectural visual landing pages and walkthroughs",
      "Multi-channel drip nurture sequences to accelerate sales cycle velocity",
    ],
  },
  {
    name: "Healthcare & Pharma",
    tag: "Compliant Media · Patient Trust",
    icon: Stethoscope,
    gradient: "linear-gradient(135deg, oklch(0.48 0.18 245), oklch(0.35 0.15 275))",
    shadow: "rgba(37, 99, 235, 0.4)",
    overview:
      "Trust-first healthcare marketing adhering to medical regulatory standards. Building patient awareness, practitioner credibility, and specialized clinical footfall.",
    channels: ["Search & SEO", "Local OOH & Clinics", "Educational Video", "Reputation ORM"],
    metric: "Brand Trust",
    highlights: [
      "Strict compliance with medical advertising regulations and claims guidelines",
      "Hyper-local medical search optimization for regional clinics and hospitals",
      "Executive ORM and doctor thought leadership content production",
    ],
  },
  {
    name: "E-Commerce & D2C",
    tag: "D2C Sales · Conversion Velocity",
    icon: Zap,
    gradient: "linear-gradient(135deg, oklch(0.65 0.2 140), oklch(0.5 0.18 160))",
    shadow: "rgba(168, 85, 247, 0.4)",
    overview:
      "Full-funnel digital campaigns engineered for maximum purchase velocity, blended CAC reduction, and compounding customer lifetime value across global marketplaces.",
    channels: ["Google Shopping & PMax", "Meta Advantage+", "Email Automation", "TikTok & Reels"],
    metric: "ROAS Scaling",
    highlights: [
      "Continuous multivariate creative testing across hooks, angles, and formats",
      "Automated lifecycle email and SMS retention workflows",
      "Server-side tracking and multi-touch attribution modeling",
    ],
  },
  {
    name: "Technology & SaaS",
    tag: "Product Adoption · B2B Pipeline",
    icon: Laptop,
    gradient: "linear-gradient(135deg, oklch(0.68 0.16 280), oklch(0.5 0.18 300))",
    shadow: "rgba(139, 92, 246, 0.4)",
    overview:
      "Account-based marketing, demo booking engines, and product-led growth campaigns that connect enterprise technology providers with verified decision-makers.",
    channels: ["LinkedIn Ads", "Account-Based Marketing", "Search Intent", "Content Syndication"],
    metric: "Enterprise SQLs",
    highlights: [
      "Job-title and firmographic precision targeting across LinkedIn & programmatic B2B",
      "High-value whitepaper and interactive demo lead capture architecture",
      "Full CRM pipeline synchronization with sales team attribution",
    ],
  },
  {
    name: "Financial Services & BFSI",
    tag: "Regulated Acquisition · Security",
    icon: Landmark,
    gradient: "linear-gradient(135deg, oklch(0.7 0.18 80), oklch(0.55 0.16 60))",
    shadow: "rgba(245, 158, 11, 0.4)",
    overview:
      "Secure, high-trust acquisition funnels for fintech platforms, wealth management institutions, and insurance providers seeking qualified account holders.",
    channels: ["Financial Display", "Search Ads", "Print Financials", "Web Security CRO"],
    metric: "Low-CPA Accounts",
    highlights: [
      "Strict data privacy compliance and verified lead authentication",
      "High-intent financial keyword bidding strategies with negative term filtering",
      "Conversion-optimized onboarding flows with minimal drop-off friction",
    ],
  },
];

export function WorkPage() {
  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-16 md:py-24 border-b border-white/5">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-orange)]" />
          <div className="glow-orb right-[-100px] top-30 h-[380px] w-[380px] bg-[var(--glow-purple)]" />

          <div className="container-x relative z-10">
            {/* Breadcrumbs */}
            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-[var(--glow-orange)] font-semibold">Industries & Work</span>
            </div>

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-orange)] shadow-[0_0_8px_var(--glow-orange)] animate-pulse" />
                VERTICAL MEDIA EXPERTISE
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Industries We Scale Into <br />
                <span className="text-gradient">High-Velocity Engines.</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg md:text-xl text-white/90 font-medium leading-relaxed">
                We don’t do generic, one-size-fits-all marketing. We engineer vertical-specific media systems that combine audience diagnostics, channel negotiation, and performance optimization.
              </p>

              <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Whether launching national prime-time broadcast campaigns, driving high-intent B2B pipelines, or scaling D2C e-commerce revenue, our team brings 25+ years of strategic execution to every vertical.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/contact-us" className="btn-hero !py-3 !px-7">
                  Consult on Your Vertical <ArrowRight className="h-4 w-4 ml-1 inline" />
                </Link>
                <Link to="/process" className="btn-ghost !py-3 !px-6">
                  Explore Our Process <ArrowUpRight className="h-4 w-4 ml-1 inline" />
                </Link>
              </div>
            </div>

            {/* Quick Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 pt-8 border-t border-white/10">
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-white">8+</div>
                <div className="text-xs text-muted-foreground mt-1">Core Industry Verticals</div>
              </div>
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-[var(--glow-orange)]">25+ Yrs</div>
                <div className="text-xs text-muted-foreground mt-1">Media Buying Heritage</div>
              </div>
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-white">360°</div>
                <div className="text-xs text-muted-foreground mt-1">Omnichannel Reach</div>
              </div>
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-[var(--glow-purple)]">Global</div>
                <div className="text-xs text-muted-foreground mt-1">Asia, UAE & Europe</div>
              </div>
            </div>
          </div>
        </section>

        {/* INTERACTIVE 3D CYLINDER / SHOWCASE SECTION */}
        <Work />

        {/* DETAILED INDUSTRY BREAKDOWN GRID */}
        <section className="py-16 md:py-24 bg-black/60 border-t border-b border-white/5">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--glow-orange)] font-semibold mb-3">
                <Layers className="h-3.5 w-3.5" />
                COMPREHENSIVE VERTICAL SYSTEMS
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                Tailored Playbooks for <span className="text-gradient">Every Sector.</span>
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground">
                Each industry operates with distinct customer behavior, regulatory constraints, and purchase lifecycles. Here is how we deploy channel strategies for each market.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {industryDetails.map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={item.name}
                    className="rounded-3xl p-7 sm:p-8 bg-[#08080d] border border-white/10 flex flex-col justify-between relative overflow-hidden shadow-2xl hover:border-white/20 transition-all duration-300 group"
                  >
                    <div
                      className="absolute top-0 right-0 h-40 w-40 blur-3xl opacity-10 group-hover:opacity-25 transition-opacity pointer-events-none"
                      style={{ background: item.shadow }}
                    />

                    <div>
                      {/* Header */}
                      <div className="flex items-center justify-between gap-4 mb-5">
                        <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white shrink-0 group-hover:scale-105 transition-transform">
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <span className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground bg-white/5 px-3 py-1 rounded-md border border-white/5">
                          {item.tag}
                        </span>
                      </div>

                      <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-2">
                        {item.name}
                      </h3>

                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
                        {item.overview}
                      </p>

                      {/* Capabilities Highlights */}
                      <div className="space-y-2 mb-6">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-white/70 block">
                          Key Strategic Deliverables:
                        </span>
                        {item.highlights.map((h, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-slate-300">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--glow-orange)] shrink-0 mt-0.5" />
                            <span>{h}</span>
                          </div>
                        ))}
                      </div>

                      {/* Channel Mix Tags */}
                      <div className="mb-6 pt-4 border-t border-white/5">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-2">
                          Deployed Channel Mix:
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {item.channels.map((ch) => (
                            <span
                              key={ch}
                              className="text-[11px] bg-white/[0.04] border border-white/10 px-2.5 py-1 rounded-lg text-slate-300 font-medium"
                            >
                              {ch}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between border-t border-white/5 pt-4 mt-2">
                      <div>
                        <div className="text-[10px] font-mono text-muted-foreground">PERFORMANCE BENCHMARK</div>
                        <div
                          className="text-sm font-bold text-gradient"
                          style={{ backgroundImage: item.gradient }}
                        >
                          {item.metric}
                        </div>
                      </div>
                      <Link
                        to="/contact-us"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/90 hover:text-[var(--glow-orange)] transition-colors"
                      >
                        Plan Campaign <ArrowUpRight className="h-4 w-4 text-[var(--glow-orange)]" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* GLOBAL FOOTPRINT */}
        <BrandMarquee />

        {/* CALL TO ACTION */}
        <section className="py-20 md:py-28 relative overflow-hidden bg-black border-t border-white/5">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />
          <div className="container-x relative z-10 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[var(--glow-orange)] border border-white/10 mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              SCALE YOUR VERTICAL TODAY
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
              Ready to Build a High-ROAS Media Engine for Your Brand?
            </h2>

            <p className="mt-5 text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Book a strategic consultation with our senior media planners to discuss audience diagnostics, rate benchmarks, and custom multi-channel flight plans.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact-us" className="btn-hero !py-3.5 !px-8 text-sm sm:text-base">
                Request Media Strategy <ArrowRight className="h-4 w-4 ml-1 inline" />
              </Link>
              <Link to="/services" className="btn-ghost !py-3.5 !px-7 text-sm sm:text-base">
                View All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
